import { DepartmentLabel, useDepartmentText } from 'model/DepartmentTerminology';
import { useForm } from '@mantine/form';
import createValidator from 'components/forms/validators/create-validator';
import required from 'components/forms/validators/rules/rule-required';
import emailFormat from 'components/forms/validators/rules/rule-email-format';
import { isEmpty, noop } from 'lodash';
import { Anchor, Box, Group, PasswordInput, SimpleGrid, Stack, Switch, TextInput } from '@mantine/core';
import { FormMessage, FormProps } from 'components/forms/FormProps';
import useLoadingAction from 'hooks/use-loading-action';
import FormInputGroup from 'components/forms/FormInputGroup';
import { RoleSelect } from 'components/selects/RoleSelect';
import { OrganizationSelect } from 'components/selects/OrganizationSelect';
import FormWrapper from 'components/forms/FormWrapper';
import DateDisplay from 'components/DateDisplay';
import passwordFormat from 'components/forms/validators/rules/rule-password-format';
import AdminChangePasswordForm, { AdminChangePasswordFormData } from 'components/forms/user/AdminChangePasswordForm';
import { IconCheck, IconPlus } from '@tabler/icons-react';
import P1Regular from 'components/typography/P1Regular';
import P1RegularLink from 'components/typography/P1RegularLink';
import PermissionsForEntity from 'components/PermissionsForEntity';
import { usePickOrganizations } from 'components/modals/pick-organizations-modal/PickOrganizationsProvider';
import { usePickDepartments } from 'components/modals/pick-departments-modal/PickDepartmentsProvider';
import { useApi } from 'api/api-context';
import { useEffect, useMemo, useState } from 'react';
import { ROLE_ADMIN_ID, ROLE_USER_ID } from 'model/Role';
import { useDirtyFormHandler } from 'hooks/use-dirty-form-handler';
import { UserSelect } from 'components/selects/UserSelect';
import { useResponsiveDesign } from 'hooks/use-responsive-design';
import Button from 'components/Button';
import GlobalPermissionsForUser from 'components/forms/user/GlobalPermissionsForUser';
import { shouldShowGlobalPermissionsSection } from 'components/forms/user/user-form-helpers';
import { OrganizationModuleFlags } from 'features/organization-modules/use-organization-modules';
import { DepartmentBreadcrumbItem, formatDepartmentPath } from 'components/department/DepartmentBreadcrumb';
import useLocalStorage from 'hooks/use-local-storage';
import P1Medium from 'components/typography/P1Medium';
import { FEATURE_TOGGLE_USER_NFQES_EMAIL } from 'env';

/**
 * Data collected from the user form.
 */
export interface UserFormData {
  organizationId: string;
  roleId: string;
  preNominals: string;
  postNominals: string;
  firstName: string;
  lastName: string;
  email: string;
  nfqesEmail?: string;
  password: string;
  confirmPassword: string;
  phoneNumber: string;
  copyProfilesFromUserId?: string;
  globalPermissions: number[];
  inOrganizations: {
    [organizationId: number]: {
      permissions: number[];
      templateName: string;
      checked: boolean;
      organizationName: string;
    };
  };
  inDepartments: {
    [departmentId: number]: {
      permissions: number[];
      templateName: string;
      checked: boolean;
      departmentName: string;
      breadcrumbs?: DepartmentBreadcrumbItem[];
    };
  };
  inheritedDepartments?: {
    departmentId: number;
    departmentName: string;
    breadcrumbs: DepartmentBreadcrumbItem[];
    permissions: number[];
    templateName: string;
    sourceDepartmentId: number;
    sourceDepartmentName: string;
    sourceDepartmentPath: string;
  }[];
}

/**
 * Initial values of the user form.
 */
export const userFormInitialValues: UserFormData = {
  preNominals: '',
  postNominals: '',
  firstName: '',
  lastName: '',
  email: '',
  nfqesEmail: '',
  phoneNumber: '',
  password: '',
  confirmPassword: '',
  organizationId: '',
  roleId: '',
  globalPermissions: [],
  inOrganizations: {},
  inDepartments: {},
  inheritedDepartments: [],
};

/**
 * Parameters of the UserForm component.
 */
export interface UserFormProps extends FormProps<UserFormData> {
  hideSections?: Partial<{
    password?: boolean;
    inOrganizations?: boolean;
    inDepartments?: boolean;
    tableProfiles?: boolean;
  }>;
  onPasswordChange?: (data: AdminChangePasswordFormData) => void;
  passwordChangeMessage?: FormMessage;
  primaryButtonText?: string;
  primaryIcon?: React.ReactNode;
  readOnly?: Partial<Record<keyof UserFormData, boolean>>;
  passwordValidUntil?: string;
}

/**
 * Form used to create a new user or update an existing user.
 */
export default function UserForm({
  initialValues = userFormInitialValues,
  onSubmit = noop,
  onPasswordChange = noop,
  passwordChangeMessage,
  hideSections = {},
  primaryButtonText = 'Uložiť',
  primaryIcon = <IconCheck stroke="1.5" height={24} width={24} />,
  readOnly = {},
  passwordValidUntil,
}: UserFormProps = {}) {
  const departmentText = useDepartmentText();
  const [{ loading }, submit] = useLoadingAction(onSubmit);
  const { role, roleId, getAction } = useApi();
  const { pickOrganizations } = usePickOrganizations();
  const { pickDepartments } = usePickDepartments();
  const { isTablet } = useResponsiveDesign();
  const [directOrganizationModuleFlags, setDirectOrganizationModuleFlags] = useState<
    Record<number, OrganizationModuleFlags>
  >({});
  const [permissionVisibility, , updatePermissionVisibility] = useLocalStorage(
    'objekto.permissions.hide-inherited-only',
    { hideInheritedOnly: false }
  );

  const form = useForm<UserFormData>({
    initialValues,
    validate: {
      firstName: createValidator([required]),
      lastName: createValidator([required]),
      email: createValidator([required, emailFormat]),
      nfqesEmail: FEATURE_TOGGLE_USER_NFQES_EMAIL
        ? (value) => (value?.trim() ? createValidator([emailFormat])(value.trim()) : null)
        : undefined,
      password: hideSections.password ? undefined : createValidator([required, passwordFormat]),
      confirmPassword: hideSections.password
        ? undefined
        : (confirmPassword, { password }) => {
            if (!confirmPassword) {
              return 'Pole je povinné';
            }

            if (confirmPassword !== password) {
              return 'Heslá sa nezhodujú';
            }

            return null;
          },
      organizationId: createValidator([required]),
      roleId: createValidator([required]),
    },
  });

  useDirtyFormHandler(form);

  /**
   * Callback for when user clicks on the "Pridať oprávnenia v organizáciách" button.
   */
  const assignOrganizations = () => {
    pickOrganizations({
      title: 'Pridať oprávenia v organizáciach',
      hiddenOrganizations: Object.keys(form.values.inOrganizations)
        .filter((organizationId) => form.values.inOrganizations?.[Number(organizationId)].checked)
        .map(Number),
      onPick: ({ pickedOrganizations, permissions, templateName }) => {
        Object.keys(pickedOrganizations).forEach((organizationId: string) => {
          const pickedOrganization = pickedOrganizations[Number(organizationId)];

          form.setFieldValue(`inOrganizations.${organizationId}`, {
            checked: pickedOrganization.checked,
            permissions,
            templateName,
            organizationName: pickedOrganization.organizationName,
          });
        });
      },
      initialTemplateName: 'no-template',
      initialPermissions: [],
      pickOrganizations: true,
      pickPermissions: true,
    });
  };

  /**
   * Callback for when user clicks on the "Pridať oprávnenia v položkách úrovne" button.
   */
  const assignDepartments = () => {
    const inheritedPermissionsByDepartment = (form.values.inheritedDepartments ?? []).reduce(
      (permissionsByDepartment, department) => {
        const sourceDepartment = form.values.inDepartments[department.sourceDepartmentId];

        if (!sourceDepartment?.checked) return permissionsByDepartment;

        permissionsByDepartment[department.departmentId] = Array.from(
          new Set([...(permissionsByDepartment[department.departmentId] ?? []), ...sourceDepartment.permissions])
        );
        return permissionsByDepartment;
      },
      {} as Record<number, number[]>
    );

    pickDepartments({
      title: (
        <DepartmentLabel
          objectLabel="Pridať oprávenia v položkách úrovne"
          departmentLabel="Pridať oprávenia v strediskách"
        />
      ),
      hiddenDepartments: Object.keys(form.values.inDepartments)
        .filter((departmentId) => form.values.inDepartments?.[Number(departmentId)].checked)
        .map(Number),
      onPick: ({ pickedDepartments, permissions, templateName }) => {
        Object.keys(pickedDepartments).forEach((departmentId: string) => {
          const pickedDepartment = pickedDepartments[Number(departmentId)];

          form.setFieldValue(`inDepartments.${departmentId}`, {
            checked: pickedDepartment.checked,
            permissions,
            templateName,
            departmentName: pickedDepartment.departmentName,
            breadcrumbs: pickedDepartment.breadcrumbs,
          });
        });
      },
      initialTemplateName: 'no-template',
      initialPermissions: [],
      inheritedPermissionsByDepartment,
      pickDepartments: true,
      pickPermissions: true,
    });
  };

  // non-admin users can only create users with role "Používateľ"
  useEffect(() => {
    if (role !== 'Admin') {
      form.setFieldValue('roleId', String(ROLE_USER_ID));
    }
  }, [role]);

  useEffect(() => {
    if (roleId !== ROLE_ADMIN_ID || !getAction) {
      return;
    }

    getAction('OrganizationList')({ query: { filters: { 'showDiscarded.eq': 1 } } })
      .then((organizations) => {
        setDirectOrganizationModuleFlags(
          organizations.reduce((flags, organization) => {
            flags[organization.organizationId] = {
              priceOffersEnabled: organization.priceOffersEnabled,
              hrEnabled: organization.hrEnabled,
              revisionsEnabled: organization.revisionsEnabled,
              formsEnabled: organization.formsEnabled,
              revisionTemplatesEnabled: organization.revisionTemplatesEnabled,
            };
            return flags;
          }, {} as Record<number, OrganizationModuleFlags>)
        );
      })
      .catch(noop);
  }, [getAction, roleId]);

  const displayedDepartments = useMemo(() => {
    const departments = new Map<
      number,
      {
        departmentId: number;
        departmentName: string;
        breadcrumbs: DepartmentBreadcrumbItem[];
        direct?: UserFormData['inDepartments'][number];
        inheritedSources: {
          key: string;
          label: string;
          permissions: number[];
          templateSlug: string;
        }[];
      }
    >();

    (form.values.inheritedDepartments ?? []).forEach((department) => {
      const sourceDepartment = form.values.inDepartments[department.sourceDepartmentId];

      if (!sourceDepartment?.checked) return;

      const displayed = departments.get(department.departmentId) ?? {
        departmentId: department.departmentId,
        departmentName: department.departmentName,
        breadcrumbs: department.breadcrumbs,
        inheritedSources: [],
      };
      displayed.inheritedSources.push({
        key: `department-${department.sourceDepartmentId}`,
        label: departmentText(
          `Oprávnenia z položky úrovne ${department.sourceDepartmentPath}`,
          `Oprávnenia zo strediska ${department.sourceDepartmentPath}`
        ),
        permissions: sourceDepartment.permissions,
        templateSlug: sourceDepartment.templateName,
      });
      departments.set(department.departmentId, displayed);
    });

    Object.entries(form.values.inDepartments).forEach(([departmentId, department]) => {
      if (!department.checked) return;

      const id = Number(departmentId);
      const displayed = departments.get(id) ?? {
        departmentId: id,
        departmentName: department.departmentName,
        breadcrumbs: department.breadcrumbs ?? [],
        inheritedSources: [],
      };
      displayed.direct = department;
      departments.set(id, displayed);
    });

    return Array.from(departments.values()).sort((left, right) =>
      (formatDepartmentPath(left.breadcrumbs) || left.departmentName).localeCompare(
        formatDepartmentPath(right.breadcrumbs) || right.departmentName,
        'sk'
      )
    );
  }, [departmentText, form.values.inDepartments, form.values.inheritedDepartments]);
  const editableDisplayedDepartments = permissionVisibility.hideInheritedOnly
    ? displayedDepartments.filter(({ direct }) => Boolean(direct?.permissions.length))
    : displayedDepartments;
  const inheritedOnlyDepartments = permissionVisibility.hideInheritedOnly
    ? displayedDepartments.filter(
        ({ direct, inheritedSources }) =>
          !direct?.permissions.length && inheritedSources.some(({ permissions }) => permissions.length > 0)
      )
    : [];

  return (
    <FormWrapper
      onSubmit={form.onSubmit(submit)}
      loading={loading}
      primaryButtonText={primaryButtonText}
      primaryIcon={primaryIcon}
      skipSecondaryButtonConfirm={!form.isDirty()}
    >
      <FormInputGroup groupTitle="Prístupové práva">
        <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
          <OrganizationSelect
            label="Organizácia"
            size="lg"
            placeholder="Vyberte organizáciu"
            showDiscarded={false}
            readOnly={readOnly.organizationId}
            disabled={readOnly.organizationId}
            permissionSlug="manage-users"
            autoSelectSingleResult
            name="objekto-organizationId"
            {...form.getInputProps('organizationId')}
          />
          {role === 'Admin' && (
            <RoleSelect
              label="Rola"
              size="lg"
              placeholder="Vyberte rolu"
              name="objekto-roleId"
              {...form.getInputProps('roleId')}
            />
          )}
        </SimpleGrid>
      </FormInputGroup>
      <FormInputGroup groupTitle="Osobné údaje">
        <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
          <TextInput
            size="lg"
            label="Titul pred menom"
            placeholder="Titul pred menom"
            name="objekto-preNominals"
            {...form.getInputProps('preNominals')}
          />
          <TextInput
            size="lg"
            label="Titul za menom"
            placeholder="Titul za menom"
            name="objekto-postNominals"
            {...form.getInputProps('postNominals')}
          />
        </SimpleGrid>
        <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
          <TextInput
            size="lg"
            label="Meno"
            placeholder="Meno"
            name="objekto-firstName"
            {...form.getInputProps('firstName')}
          />
          <TextInput
            size="lg"
            label="Priezvisko"
            placeholder="Priezvisko"
            name="objekto-lastName"
            {...form.getInputProps('lastName')}
          />
        </SimpleGrid>
        <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
          <TextInput
            size="lg"
            type="email"
            label="E-mail"
            placeholder="Emailová adresa"
            name="objekto-email"
            {...form.getInputProps('email')}
          />
          <TextInput
            size="lg"
            label="Telefón"
            type="tel"
            placeholder="Telefónne číslo"
            name="objekto-phoneNumber"
            {...form.getInputProps('phoneNumber')}
          />
        </SimpleGrid>
      </FormInputGroup>

      {FEATURE_TOGGLE_USER_NFQES_EMAIL && (
        <FormInputGroup groupTitle="Elektronický podpis">
          <TextInput
            label="Mailový účet používaný na službe elektronického podpisu"
            size="lg"
            type="email"
            name="objekto-nfqesEmail"
            {...form.getInputProps('nfqesEmail')}
          />
        </FormInputGroup>
      )}

      {/* Hidden in the case of Edit displayed in case of Add */}
      {!hideSections.password && (
        <FormInputGroup groupTitle="Heslo">
          <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
            <PasswordInput
              label="Heslo"
              placeholder="Zadajte heslo"
              size="lg"
              name="objekto-password"
              {...form.getInputProps('password')}
            />
            <PasswordInput
              label="Potvrďte heslo"
              placeholder="Zadajte heslo znova"
              size="lg"
              name="objekto-confirmPassword"
              {...form.getInputProps('confirmPassword')}
            />
          </SimpleGrid>
        </FormInputGroup>
      )}

      {!hideSections.tableProfiles && (
        <FormInputGroup groupTitle="Tabuľkové profily">
          <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
            <UserSelect
              label="Skopírovať od používateľa (nepovinné)"
              size="lg"
              placeholder="Vyberte používateľa"
              name="objekto-copyFromUserId"
              showDiscarded={false}
              {...form.getInputProps('copyProfilesFromUserId')}
            />
          </SimpleGrid>
        </FormInputGroup>
      )}

      {/* Displayed in the case of Edit and hidden in case of Add */}
      {hideSections.password && roleId === ROLE_ADMIN_ID && passwordValidUntil && (
        <FormInputGroup groupTitle="Platnosť hesla">
          <DateDisplay date={new Date(passwordValidUntil)} withTime withRelative />
        </FormInputGroup>
      )}
      {hideSections.password && roleId === ROLE_ADMIN_ID && (
        <AdminChangePasswordForm onSubmit={onPasswordChange} message={passwordChangeMessage} />
      )}

      {shouldShowGlobalPermissionsSection(roleId, form.values.roleId) && (
        <FormInputGroup groupTitle="Globálne oprávnenia">
          <GlobalPermissionsForUser
            permissions={form.values.globalPermissions}
            onChange={(globalPermissions) => form.setFieldValue('globalPermissions', globalPermissions)}
            moduleFlags={directOrganizationModuleFlags[Number(form.values.organizationId)]}
          />
        </FormInputGroup>
      )}

      {form.values.roleId !== String(ROLE_ADMIN_ID) && (
        <>
          <FormInputGroup groupTitle="Používateľ v organizáciách">
            {(isEmpty(form.values.inOrganizations) ||
              Object.keys(form.values.inOrganizations)
                .map((organizationId) => form.values.inOrganizations[Number(organizationId)].checked)
                .every((checked) => !checked)) && (
              <Group pt={26} spacing={8}>
                <P1Regular color="gray.6">Používateľ zatiaľ nemá oprávnenia žiadnych organizáciách.</P1Regular>
                <Anchor onClick={assignOrganizations}>
                  <P1RegularLink span color="red.8">
                    Priraďte používateľovi práva v organizáciách
                  </P1RegularLink>
                </Anchor>
              </Group>
            )}

            <SimpleGrid cols={2} spacing={isTablet ? 20 : 40} verticalSpacing={24}>
              {Object.keys(form.values.inOrganizations).map((organizationId) => {
                const organizationEntry = form.values.inOrganizations[organizationId as any];

                return (
                  organizationEntry.checked && (
                    <PermissionsForEntity
                      key={organizationId}
                      entityId={Number(organizationId)}
                      entityName={organizationEntry.organizationName}
                      entityType="organization"
                      permissions={organizationEntry.permissions}
                      templateSlug={organizationEntry.templateName}
                      {...form.getInputProps('inOrganizations')}
                      updatePermissionsForEntity={({ permissions, templateName }) => {
                        form.setFieldValue(`inOrganizations.${organizationId}.permissions`, permissions);

                        form.setFieldValue(`inOrganizations.${organizationId}.templateName`, templateName);
                      }}
                      onEntityDelete={(entityId) => {
                        form.setFieldValue(`inOrganizations.${entityId}`, {
                          checked: false,
                          permissions: [],
                        });
                      }}
                    />
                  )
                );
              })}
            </SimpleGrid>
          </FormInputGroup>

          {!isEmpty(form.values.inOrganizations) &&
            Object.keys(form.values.inOrganizations)
              .map((organizationId) => form.values.inOrganizations[Number(organizationId)].checked)
              .some((checked) => checked) && (
              <Box pl={isTablet ? 20 : 40} pb={24}>
                <Button size="md" variant="subtle" leftIcon={<IconPlus stroke="1.5" />} onClick={assignOrganizations}>
                  Pridať oprávnenia v organizáciách
                </Button>
              </Box>
            )}
        </>
      )}

      {form.values.roleId !== String(ROLE_ADMIN_ID) && (
        <>
          <FormInputGroup
            groupTitle={departmentText('Používateľ v položkách úrovne', 'Používateľ v strediskách')}
            infoBox={
              <Box ml="auto">
                <Switch
                  checked={permissionVisibility.hideInheritedOnly}
                  label="Skryť položky iba so zdedenými oprávneniami"
                  onChange={(event) => updatePermissionVisibility({ hideInheritedOnly: event.currentTarget.checked })}
                />
              </Box>
            }
          >
            {displayedDepartments.length === 0 && (
              <Group pt={26} spacing={8}>
                <P1Regular color="gray.6">
                  {departmentText(
                    'Používateľ zatiaľ nemá oprávnenia v žiadnych položkách úrovne.',
                    'Používateľ zatiaľ nemá oprávnenia v žiadnych strediskách.'
                  )}
                </P1Regular>
                <Anchor onClick={assignDepartments}>
                  <P1RegularLink span color="red.8">
                    {departmentText('Pridať oprávnenia v položkách úrovne', 'Pridať oprávnenia v strediskách')}
                  </P1RegularLink>
                </Anchor>
              </Group>
            )}

            <SimpleGrid
              data-testid="user-department-permission-grid"
              data-layout-columns={isTablet ? 1 : 2}
              cols={isTablet ? 1 : 2}
              spacing={isTablet ? 20 : 40}
              verticalSpacing={24}
            >
              {editableDisplayedDepartments.map(
                ({ departmentId, departmentName, breadcrumbs, direct, inheritedSources }) => (
                  <PermissionsForEntity
                    key={departmentId}
                    entityId={departmentId}
                    entityName={departmentName}
                    entityType="department"
                    departmentBreadcrumbs={breadcrumbs}
                    permissions={direct?.permissions ?? []}
                    templateSlug={direct?.templateName ?? 'no-template'}
                    hideDelete={!direct}
                    summarizeInheritedPermissions={
                      permissionVisibility.hideInheritedOnly &&
                      inheritedSources.some(({ permissions }) => permissions.length > 0)
                    }
                    permissionSources={
                      inheritedSources.length > 0
                        ? [
                            ...inheritedSources,
                            ...(direct
                              ? [
                                  {
                                    key: 'direct',
                                    label: 'Priame oprávnenia',
                                    permissions: direct.permissions,
                                    templateSlug: direct.templateName,
                                  },
                                ]
                              : []),
                          ]
                        : undefined
                    }
                    {...form.getInputProps('inDepartments')}
                    updatePermissionsForEntity={({ permissions, templateName }) => {
                      form.setFieldValue(`inDepartments.${departmentId}`, {
                        checked: true,
                        departmentName,
                        breadcrumbs,
                        permissions,
                        templateName,
                      });
                    }}
                    onEntityDelete={(entityId) => {
                      form.setFieldValue(`inDepartments.${entityId}`, {
                        ...form.values.inDepartments[entityId],
                        checked: false,
                        permissions: [],
                      });
                    }}
                  />
                )
              )}
            </SimpleGrid>

            {inheritedOnlyDepartments.length > 0 && (
              <Stack mt={24} spacing={8}>
                <P1Medium color="gray.8">Iba so zdedenými oprávneniami</P1Medium>
                <Group spacing={8}>
                  {inheritedOnlyDepartments.map(({ departmentId, departmentName, breadcrumbs, inheritedSources }) => (
                    <PermissionsForEntity
                      key={departmentId}
                      compact
                      entityId={departmentId}
                      entityName={departmentName}
                      entityType="department"
                      departmentBreadcrumbs={breadcrumbs}
                      permissions={[]}
                      templateSlug="no-template"
                      permissionSources={inheritedSources}
                      value={form.values.inDepartments}
                      updatePermissionsForEntity={noop}
                      onEntityDelete={noop}
                    />
                  ))}
                </Group>
              </Stack>
            )}
          </FormInputGroup>

          {displayedDepartments.length > 0 && (
            <Box pl={isTablet ? 20 : 40} pb={24}>
              <Button size="md" variant="subtle" leftIcon={<IconPlus stroke="1.5" />} onClick={assignDepartments}>
                {departmentText('Pridať oprávnenia v položkách úrovne', 'Pridať oprávnenia v strediskách')}
              </Button>
            </Box>
          )}
        </>
      )}
    </FormWrapper>
  );
}
